import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import Button from '@components/common/Button/Button'
import Input from '@components/common/Input/Input'
import { validateEmail, isRequired } from '@utils/validators'
import authService from '@services/authService'
import './ForgotPassword.css'

/**
 * Forgot Password Page Component
 *
 * Allows users to request a password reset link via email.
 *
 * SECURITY FEATURES:
 * - Rate limiting feedback from backend
 * - No email existence disclosure (always shows success)
 * - Client-side validation
 *
 * @component
 */
const ForgotPassword = () => {
	// Form state
	const [email, setEmail] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [isSubmitted, setIsSubmitted] = useState(false)

	// Validation state
	const [error, setError] = useState('')
	const [touched, setTouched] = useState(false)

	// Rate limit state
	const [isRateLimited, setIsRateLimited] = useState(false)
	const [retryAfter, setRetryAfter] = useState(0)

	/**
	 * Validate email field
	 */
	const validateEmailField = (value) => {
		if (!isRequired(value)) {
			return 'Email is required'
		}
		if (!validateEmail(value)) {
			return 'Please enter a valid email address'
		}
		return ''
	}

	/**
	 * Handle email input change
	 */
	const handleEmailChange = (e) => {
		const value = e.target.value.trim()
		setEmail(value)

		if (touched) {
			setError(validateEmailField(value))
		}
	}

	/**
	 * Handle field blur
	 */
	const handleBlur = () => {
		setTouched(true)
		setError(validateEmailField(email))
	}

	/**
	 * Handle form submission
	 */
	const handleSubmit = async (e) => {
		e.preventDefault()

		// Mark as touched and validate
		setTouched(true)
		const validationError = validateEmailField(email)
		setError(validationError)

		if (validationError) {
			return
		}

		setIsLoading(true)

		try {
			await authService.forgotPassword(email.toLowerCase())

			// Always show success (for security - don't reveal if email exists)
			setIsSubmitted(true)
			toast.success('Password reset instructions sent!')
		} catch (error) {
			console.error('Forgot password error:', error)

			// Check for rate limiting
			if (error.status === 429) {
				setIsRateLimited(true)
				setRetryAfter(error.data?.retry_after || 3600)
				toast.error(error.message || 'Too many requests. Please try again later.')
			} else {
				// For other errors, still show generic success for security
				setIsSubmitted(true)
			}
		} finally {
			setIsLoading(false)
		}
	}

	/**
	 * Format retry time
	 */
	const formatRetryTime = (seconds) => {
		const minutes = Math.ceil(seconds / 60)
		return `${minutes} minute${minutes !== 1 ? 's' : ''}`
	}

	// Success state - email sent
	if (isSubmitted) {
		return (
			<div className="forgot-password-page">
				<div className="forgot-password-container">
					<div className="forgot-password-card">
						{/* Success Icon */}
						<div className="success-icon">
							<i className="icon-check-circle" aria-hidden="true"></i>
						</div>

						{/* Success Message */}
						<div className="forgot-password-header">
							<h1 className="forgot-password-title">Check Your Email</h1>
							<p className="forgot-password-subtitle">
								If an account exists for <strong>{email}</strong>, you will receive
								a password reset link shortly.
							</p>
						</div>

						{/* Instructions */}
						<div className="forgot-password-instructions">
							<h3>What's next?</h3>
							<ol>
								<li>Check your inbox (and spam folder)</li>
								<li>Click the reset link in the email</li>
								<li>Create a new password</li>
							</ol>
						</div>

						{/* Info Box */}
						<div className="info-box">
							<i className="icon-info" aria-hidden="true"></i>
							<p>
								The reset link will expire in <strong>1 hour</strong> for security reasons.
							</p>
						</div>

						{/* Actions */}
						<div className="forgot-password-actions">
							<Link to="/login" className="back-to-login-link">
								<i className="icon-arrow-left" aria-hidden="true"></i>
								Back to Login
							</Link>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Rate limited state
	if (isRateLimited) {
		return (
			<div className="forgot-password-page">
				<div className="forgot-password-container">
					<div className="forgot-password-card">
						{/* Warning Icon */}
						<div className="warning-icon">
							<i className="icon-clock" aria-hidden="true"></i>
						</div>

						{/* Rate Limit Message */}
						<div className="forgot-password-header">
							<h1 className="forgot-password-title">Too Many Requests</h1>
							<p className="forgot-password-subtitle">
								You've made too many password reset requests. Please try again
								in {formatRetryTime(retryAfter)}.
							</p>
						</div>

						{/* Actions */}
						<div className="forgot-password-actions">
							<Link to="/login" className="back-to-login-link">
								<i className="icon-arrow-left" aria-hidden="true"></i>
								Back to Login
							</Link>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Default state - request form
	return (
		<div className="forgot-password-page">
			<div className="forgot-password-container">
				<div className="forgot-password-card">
					{/* Header */}
					<div className="forgot-password-header">
						{/* Logo */}
						<div className="forgot-password-logo">
							<div className="logo-placeholder">
								<i className="icon-lock" aria-hidden="true"></i>
							</div>
						</div>

						{/* Title */}
						<h1 className="forgot-password-title">Forgot Password?</h1>
						<p className="forgot-password-subtitle">
							No worries! Enter your email address and we'll send you
							instructions to reset your password.
						</p>
					</div>

					{/* Form */}
					<form className="forgot-password-form" onSubmit={handleSubmit} noValidate>
						{/* Email Input */}
						<Input
							type="email"
							id="forgot-email"
							label="Email Address"
							placeholder="Enter your email"
							value={email}
							onChange={handleEmailChange}
							onBlur={handleBlur}
							error={touched ? error : ''}
							required
							disabled={isLoading}
							autoComplete="email"
							autoFocus
						/>

						{/* Submit Button */}
						<Button
							type="submit"
							variant="primary"
							size="large"
							loading={isLoading}
							disabled={isLoading}
							className="forgot-password-button"
						>
							{isLoading ? 'Sending...' : 'Send Reset Link'}
						</Button>
					</form>

					{/* Footer */}
					<div className="forgot-password-footer">
						<Link to="/login" className="back-to-login-link">
							<i className="icon-arrow-left" aria-hidden="true"></i>
							Back to Login
						</Link>
					</div>
				</div>

				{/* Security Notice */}
				<div className="security-notice">
					<p>
						<i className="icon-shield" aria-hidden="true"></i>
						For security, password reset links expire after 1 hour
					</p>
				</div>
			</div>
		</div>
	)
}

export default ForgotPassword
